import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from "react-helmet-async";
import message from '../../utils/message';
import { getExaminationById } from "../../reduxStore/actions/examinationActionsHelper";
import { resetExaminationState } from "../../reduxStore/reducers/examinationReducersHelper";
import {
    selectCurrentExamination,
    selectExaminationLoading,
    selectExaminationError
} from "../../reduxStore/selectors/examinationSelector";
import SATExaminationContent from "../../components/SATExaminationContent/SATExaminationContent";
import ExaminationHeader from "../../components/examinationHeader/examinationHeader";
import ExaminationReadyScreen from "../../components/examinationReadyScreen/examinationReadyScreen";
import LoadingSpinner from "../../components/loadingSpinner/loadingSpinner";
import NavigationConfirmModal from "../../components/navigationConfirmModal/navigationConfirmModal";
import useNavigationGuard from "../../utils/useNavigationGuard";
import styles from "../../publicAsset/userExaminationScreen.module.scss";

const UserFullTestScreen = () => {
    const { examinationId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Redux selectors
    const examination = useSelector(selectCurrentExamination);
    const loading = useSelector(selectExaminationLoading);
    const error = useSelector(selectExaminationError);

    // Local state management
    const [isExaminationStarted, setIsExaminationStarted] = useState(false);

    // Navigation guard for active examination sessions
    const {
        showNavigationModal,
        handlePauseAndLeave,
        handleEndAndLeave,
        handleContinueSession
    } = useNavigationGuard(isExaminationStarted, examinationId);

    // Fetch examination details when component mounts
    useEffect(() => {
        if (examinationId) {
            console.log('🔍 Fetching examination by ID:', examinationId);
            dispatch(getExaminationById.request(examinationId));
        }
    }, [dispatch, examinationId]);

    // Handle examination errors
    useEffect(() => {
        if (error) {
            message.error('Failed to load examination details');
        }
    }, [error]);

    const handleStartExamination = () => {
        if (!examination) {
            message.error('Examination data not loaded');
            return;
        }
        setIsExaminationStarted(true);
    };

    const handleGoBack = () => {
        // Clear examination state first to prevent SATFullTestSelector from redirecting
        dispatch(resetExaminationState());
        // Navigate back to practice page
        navigate('/user-sat-practice', { replace: true });
    };

    const handleExaminationComplete = (results) => {
        console.log('Examination completed:', results);
        // Navigate to results page or dashboard
        navigate('/user-sat-practice', {
            state: {
                examCompleted: true,
                results
            }
        });
    };

    const handleExaminationExit = () => {
        // Handle examination exit (pause/save progress)
        navigate('/user-sat-practice');
    };

    const handlePracticeMenuClick = () => {
        // Handle menu button click during examination
        navigate('/user-sat-practice');
    };

    // Loading state
    if (loading) {
        return (
            <div className={styles.userExaminationScreen}>
                <LoadingSpinner message="Loading examination..." />
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <>
                <Helmet>
                    <title>Error - SAT Full Test | Prepal</title>
                </Helmet>
                <div className={styles.userExaminationScreen}>
                    <div className={styles.errorContainer}>
                        <h2>Error Loading Examination</h2>
                        <p>{error}</p>
                        <button onClick={handleGoBack}>
                            ← Back to SAT Practice
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>SAT Full Practice Test | Prepal</title>
                <meta name="description" content="Complete SAT Practice Test - Reading & Writing + Math sections" />
                <meta name="keywords" content="Prepal, SAT full test, practice examination" />
                <meta name="author" content="Prepal Team" />
            </Helmet>

            <div className={styles.userExaminationScreen}>
                {!isExaminationStarted ? (
                    <ExaminationReadyScreen
                        examination={examination}
                        onStartExamination={handleStartExamination}
                        onGoBack={handleGoBack}
                        disabled={!examination}
                    />
                ) : (
                    <>
                        <ExaminationHeader
                            isExamination={true}
                            onPracticeMenuClick={handlePracticeMenuClick}
                        />
                        <SATExaminationContent
                            examinationId={examinationId}
                            onComplete={handleExaminationComplete}
                            onExit={handleExaminationExit}
                        />
                    </>
                )}
            </div>

            {/* Navigation Guard Modal */}
            <NavigationConfirmModal
                isOpen={showNavigationModal}
                onPauseAndLeave={handlePauseAndLeave}
                onEndAndLeave={handleEndAndLeave}
                onContinueSession={handleContinueSession}
            />
        </>
    );
};

export default UserFullTestScreen;